import { useCallback, useEffect, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import {
  StreamableVideoSource,
  type StreamableVideoLoaders,
  type StreamableVideoStatus,
} from '../utils/streamableVideoSource';

export interface StreamableVideo {
  status: StreamableVideoStatus;
  /** Bucket stream URL or blob URL; set only when status is 'loaded'. */
  src: string | null;
  onLoadedMetadata: (event: SyntheticEvent<HTMLVideoElement>) => void;
  onError: (event: SyntheticEvent<HTMLVideoElement>) => void;
}

/**
 * Loads a recorded interview video: streams it from the bucket when the backend
 * provides a signed URL, otherwise downloads it through the API.
 * See utils/streamableVideoSource.ts.
 *
 * @param key Identifies the video; a new key loads a new video, null shows none.
 * @param loaders Read from the latest render, so inline closures are fine.
 */
export function useStreamableVideo(
  key: string | null,
  loaders: StreamableVideoLoaders,
): StreamableVideo {
  const loadersRef = useRef(loaders);
  useEffect(() => {
    loadersRef.current = loaders;
  });

  const controllerRef = useRef<StreamableVideoSource | null>(null);
  const [state, setState] = useState<{
    key: string | null;
    status: StreamableVideoStatus;
    src: string | null;
  }>({ key: null, status: 'none', src: null });

  useEffect(() => {
    if (!key) {
      setState({ key: null, status: 'none', src: null });
      return;
    }
    const controller = new StreamableVideoSource(
      () => loadersRef.current,
      (next) => setState({ key, ...next }),
    );
    controllerRef.current = controller;
    void controller.load();
    return () => {
      controller.dispose();
      if (controllerRef.current === controller) {
        controllerRef.current = null;
      }
    };
  }, [key]);

  const onLoadedMetadata = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    controllerRef.current?.handleLoadedMetadata(event.currentTarget);
  }, []);

  const onError = useCallback((event: SyntheticEvent<HTMLVideoElement>) => {
    void controllerRef.current?.handleError(event.currentTarget);
  }, []);

  // Never show the previous video's source while a new key is loading.
  const isCurrent = state.key === key;
  return {
    status: isCurrent ? state.status : key ? 'loading' : 'none',
    src: isCurrent ? state.src : null,
    onLoadedMetadata,
    onError,
  };
}
